import { useState } from "react";
import { Button, Card, ErrorNotice, Field, Input } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { useAction } from "../lib/hooks.ts";
import { useT } from "../lib/i18n.tsx";
import type { Session } from "../lib/types.ts";

/**
 * Il proprio profilo: aperto a chiunque abbia effettuato l'accesso, a
 * differenza delle Impostazioni, che chiedono un permesso.
 */
export function ProfiloPage({ sessione, onCambiato }: { sessione: Session; onCambiato: () => void }) {
  const t = useT();

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold">{t.t("profilo.titolo")}</h1>
        <p className="text-sm tenue">{t.t("profilo.intro")}</p>
      </div>

      <FormNome sessione={sessione} onCambiato={onCambiato} />
      <FormPassword onCambiato={onCambiato} />
    </div>
  );
}

function FormNome({ sessione, onCambiato }: { sessione: Session; onCambiato: () => void }) {
  const [nome, setNome] = useState(sessione.user.displayName ?? "");
  const [salvato, setSalvato] = useState(false);
  const t = useT();

  const salvataggio = useAction(async () => {
    await api.patch("/auth/profilo", { displayName: nome });
    setSalvato(true);
    onCambiato();
  });

  return (
    <Card className="space-y-4 p-5">
      <Field label={t.t("profilo.nome")} hint={t.t("profilo.nomeAiuto", { username: sessione.user.username })}>
        <Input
          value={nome}
          maxLength={100}
          onChange={(e) => {
            setNome(e.target.value);
            setSalvato(false);
          }}
        />
      </Field>

      <ErrorNotice error={salvataggio.error} />

      <div className="flex items-center gap-3">
        <Button variant="primary" loading={salvataggio.running} onClick={() => void salvataggio.run()}>
          {t.t("profilo.salvaNome")}
        </Button>
        {salvato && <span className="text-sm tenue">{t.t("profilo.nomeSalvato")}</span>}
      </div>
    </Card>
  );
}

function FormPassword({ onCambiato }: { onCambiato: () => void }) {
  const [attuale, setAttuale] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const t = useT();
  const diverse = conferma !== "" && nuova !== conferma;

  // Il server chiude tutte le sessioni: rileggere la sessione riporta all'accesso.
  const cambio = useAction(async () => {
    await api.post("/auth/password", { currentPassword: attuale, newPassword: nuova });
    onCambiato();
  });

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="font-medium">{t.t("profilo.password")}</h2>
        <p className="text-sm tenue">{t.t("profilo.passwordIntro")}</p>
      </div>

      <Field label={t.t("profilo.passwordAttuale")}>
        <Input type="password" autoComplete="current-password" value={attuale} onChange={(e) => setAttuale(e.target.value)} />
      </Field>
      <Field label={t.t("profilo.passwordNuova")} hint={t.t("utenti.passwordAiuto")}>
        <Input type="password" autoComplete="new-password" value={nuova} onChange={(e) => setNuova(e.target.value)} />
      </Field>
      <Field label={t.t("profilo.passwordConferma")} hint={diverse ? t.t("profilo.passwordDiverse") : undefined}>
        <Input type="password" autoComplete="new-password" value={conferma} onChange={(e) => setConferma(e.target.value)} />
      </Field>

      <ErrorNotice error={cambio.error} />

      <Button
        variant="primary"
        disabled={!attuale || !nuova || nuova !== conferma}
        loading={cambio.running}
        onClick={() => void cambio.run()}
      >
        {t.t("profilo.cambiaPassword")}
      </Button>
    </Card>
  );
}
